// A PO's lines as a spreadsheet (DataTable's edit mode): qty, price, the vendor's SKU and name,
// promised date and line notes edit in place; a new qty follows the vendor's price breaks. MOQ and
// order-multiple problems show on the qty with a one-click fix. Items are added by searching. Each line
// shows the spec revision it carries, and a chip when a newer one was released after the PO was sent.
// Packaging --- packaging lines sit in their own band at the end, each with what it is ("Printed box", "Bag with a
// label") and its print files to download, or what's missing for the vendor to print it (a click opens where it's fixed).
// Product labels --- a line whose product carries HHL product labels has a Label download; a line that is an HHL product
// label says its size and downloads the labels of every product it labels, instead of print files. A warn chip beside
// either says what's off about the labels (no UPC, a barcode too narrow for the label…) and opens the label part.
import { AlertTriangle, Download, Package, Search, Tag, Trash2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { type LineCharge, linePrice } from "../../../../shared/charges.ts"; // Charges ---
import { priceFlag } from "../../../../shared/costs.ts"; // Production runs ---
import type { Item, Po, PoLine, VendorItem } from "../../../../shared/db.gen.ts";
import { lineAmount, qtyWarnings, repriceFor, validQty } from "../../../../shared/po.ts";
import { fileSize } from "../../../../shared/po-docs.ts";
import { useCostIndex } from "../../lib/costs.ts";
import { money, qty } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { Badge, cx, Input, ItemName, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableBar, type TableSection } from "../../ui/DataTable.tsx";
import { PaneLink } from "../../ui/SidePane.tsx";
import { stockColumns, useStockIndex } from "../../ui/StockColumns.tsx"; // Stock everywhere ---
import { toast } from "../../ui/toast.tsx";
import { itemPath } from "../ItemsPage.tsx";
import { labelPartUrl, productLabelsUrl } from "../PackagingSection.tsx";
import { downloadUrl } from "../specs/lib.ts";
import type { LineSpec } from "./docs.ts";
import { addLines, parseNumber } from "./lib.ts";
import {
  fixTitle,
  gapSentence,
  LABEL_FIX_TITLE,
  type LineLabels,
  type LinePacking,
  leftoverNote,
  noFilesNote,
  PRINT_FILE_LABELS,
  sizeNote,
  useOpenFix,
  useOpenLabel,
} from "./packaging.ts";

type Terms = Map<string, VendorItem>;

/** Charges --- a line with charges is priced by them at its new quantity (the server works it out the same way). */
function saveQty(line: PoLine, link: VendorItem | undefined, next: number, charges?: readonly LineCharge[]) {
  const unit_price = charges?.length ? linePrice(charges, next) : repriceFor(link, line, next);
  return mutate("po_line.update", { id: line.id, patch: { qty: next, ...(unit_price !== undefined ? { unit_price } : {}) } });
}

/** Packaging --- a packaging line's missing print files: opens where they're fixed, beside the PO. */
function GapButton({ p }: { p: LinePacking }) {
  const openFix = useOpenFix();
  return (
    <button
      type="button"
      className="inline-flex shrink-0 items-center gap-1 text-warn hover:underline"
      title={fixTitle(p)}
      onClick={(e) => {
        e.stopPropagation();
        openFix(p);
      }}
    >
      <AlertTriangle className="size-3.5 shrink-0" />
      <span>{gapSentence(p)}</span>
    </button>
  );
}

/** Packaging --- the band packaging lines sit in, after the others. */
const PACKAGING: TableSection = {
  key: "packaging",
  label: (
    <span className="flex items-center gap-1.5">
      <Package className="size-3.5" /> Packaging
    </span>
  ),
  order: 1,
};

export function PoLines({
  po,
  lines,
  terms,
  editable,
  specs,
  packing,
  labels,
  bar,
  fit,
}: {
  po: Po;
  lines: PoLine[];
  terms: Terms;
  editable: boolean;
  specs: Map<string, LineSpec>;
  /** Packaging --- the packaging lines (usePoPacking()). */
  packing: Map<string, LinePacking>;
  /** Product labels --- the lines with labels (usePoLabels()). */
  labels: Map<string, LineLabels>;
  /** What adds to the lines (the item search, the vendor's items, Paste lines), in the table's bar. */
  bar?: TableBar;
  /** As tall as its rows, up to this many (a make PO's few builds, under its Builds); else it fills the part. */
  fit?: number;
}) {
  const cx_ = useCostIndex(); // Production runs --- a line priced more than 5% over its planned price is flagged
  const charges = cx_.chargesByLine; // Charges --- a build priced by its charges: its price changes on the Make tab
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const sectionOf = useCallback((l: PoLine) => (packing.has(l.id) ? PACKAGING : null), [packing]);
  const stockIx = useStockIndex(); // Stock everywhere ---
  const columns = useMemo<Column<PoLine>[]>(() => {
    const text = (key: "vendor_sku" | "vendor_name" | "notes") => ({
      get: (l: PoLine) => l[key],
      save: (l: PoLine, value: string) => mutate("po_line.update", { id: l.id, patch: { [key]: value.trim() } }),
    });
    const sent = po.sent_revision !== null;
    // `note` adds to its title (Product labels --- a label part's print files the vendor no longer gets).
    const rev = (s: LineSpec, revision: NonNullable<LineSpec["revision"]>, note = "") => (
      <span
        className={cx("shrink-0", !sent && "text-muted")}
        title={
          (!sent
            ? "The revision it will be sent with"
            : s.onPage && s.vendorRevision?.id === revision.id
              ? `Sent with Rev ${revision.rev} (${s.files.length} files)`
              : `Rev ${revision.rev} goes to the vendor when rev ${po.revision} is marked as sent`) + note
        }
      >
        Rev {revision.rev}
      </span>
    );
    const newer = (s: LineSpec) =>
      s.newer && (
        <span className="truncate" title={`Rev ${s.newer.rev} released after this PO was sent: Send update moves this line to it`}>
          <Badge tone="warn">Rev {s.newer.rev} released after this PO was sent</Badge>
        </span>
      );
    const gapButton = (p: LinePacking) => <GapButton p={p} />;
    const labelLink = "inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-accent-text hover:bg-hover";
    // Product labels --- a line that is an HHL product label: its size, its revision (one from before it was an HHL
    // product label may still have print files: vendors don't get them now), and every product's label in one PDF (a
    // sample page while no BOM has it) with what's off about them, or the gap (no size yet).
    const labelPartCell = (s: LineSpec | undefined, p: LinePacking, part: Extract<LineLabels, { kind: "part" }>) => (
      <span className="flex min-w-0 items-center gap-1.5">
        {/* The link (or the gap) is never cut short: a narrow cell shortens the size first. */}
        <span className="min-w-0 truncate" title={part.size ? `${p.pack.says} (${sizeNote(part.size)})` : p.pack.says}>
          {p.pack.title}
          {part.size && <span className="text-muted"> · {sizeNote(part.size)}</span>}
        </span>
        {s?.revision && rev(s, s.revision, leftoverNote(s.files.map((f) => f.kind)))}
        {p.gaps.length > 0 ? (
          gapButton(p)
        ) : (
          <a
            href={labelPartUrl(part.item.id, { download: true })}
            title={
              part.products
                ? `Download every product's label, a page each (${sizeNote(part.size)}): HHL Ops makes them from the products`
                : "It isn't in any product's BOM yet: a sample label"
            }
            onClick={(e) => e.stopPropagation()}
            className={labelLink}
          >
            <Download className="size-3" />
            {part.products ? `Labels (${part.products} ${part.products === 1 ? "product" : "products"})` : "Sample label"}
          </a>
        )}
        <LabelWarnChip warnings={part.warnings} label={part.item} />
      </span>
    );
    // Product labels --- a product whose BOM carries HHL product labels: they download as one PDF; what's off beside it.
    const labelsLink = (l: PoLine, product: Extract<LineLabels, { kind: "product" }>) => {
      const n = product.labels.length;
      return (
        <>
          <a
            href={productLabelsUrl(l.item_id, { download: true })}
            title={`Download its product ${n === 1 ? "label" : `labels (${n})`}: ${product.labels.map((x) => sizeNote(x.size)).join(", ")}`}
            onClick={(e) => e.stopPropagation()}
            className={labelLink}
          >
            <Tag className="size-3" />
            {n === 1 ? "Label" : "Labels"}
          </a>
          <LabelWarnChip warnings={product.warnings} label={(product.labels.find((x) => x.warnings.length) ?? product.labels[0]).item} />
        </>
      );
    };
    // Packaging --- what it is, the revision, what the vendor would be missing (first, so a narrow cell never hides it;
    // when a newer revision has it, that one and Send update instead of the newer chip) and its print files to download.
    const packCell = (s: LineSpec | undefined, p: LinePacking) => {
      const note = noFilesNote(p.pack);
      return (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="shrink-0" title={p.pack.says}>
            {p.pack.title}
          </span>
          {s?.revision && rev(s, s.revision)}
          {p.gaps.length > 0
            ? gapButton(p)
            : note &&
              !p.printFiles.length && (
                <span className="truncate text-faint" title={p.pack.says}>
                  {note}
                </span>
              )}
          {p.printFiles.map((f) => (
            <a
              key={f.id}
              href={downloadUrl(f.id)}
              download={f.filename}
              title={`Download ${f.filename} (${fileSize(f.size)})`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-accent-text hover:bg-hover"
            >
              <Download className="size-3" />
              {PRINT_FILE_LABELS[f.kind] ?? f.kind}
            </a>
          ))}
          {s && !p.fixedBy && newer(s)}
        </span>
      );
    };
    return [
      { key: "position", header: "#", width: "34px", align: "right", cell: (l) => <span className="text-faint">{l.position}</span> },
      { key: "photo", header: "", width: "40px", value: () => "", cell: (l) => <Thumb src={l.photo_url} /> },
      // SKU, then Name: two columns, as in every table of items
      {
        key: "sku",
        header: "SKU",
        width: "minmax(110px,150px)",
        value: (l) => l.sku,
        cell: (l) => {
          const item = l.item_id ? itemById.get(l.item_id) : undefined;
          // Cross-links --- the SKU opens the item (the rest of the row still edits the line).
          return item ? (
            <PaneLink
              kind="item"
              id={item.id}
              to={itemPath(item)}
              className="truncate font-mono text-[12px] hover:text-accent-text hover:underline"
              title="Open the item"
            >
              {l.sku}
            </PaneLink>
          ) : (
            <span className="truncate font-mono text-[12px]">{l.sku}</span>
          );
        },
      },
      {
        key: "name",
        header: "Name",
        width: "minmax(170px,1.4fr)",
        value: (l) => l.name,
        cell: (l) => <ItemName name={l.name} item={l.item_id ? itemById.get(l.item_id) : undefined} />,
      },
      {
        key: "vendor_sku",
        header: "Vendor SKU",
        width: "110px",
        optional: true,
        cell: (l) => <span className="font-mono text-[12px]">{l.vendor_sku}</span>,
        edit: text("vendor_sku"),
      },
      { key: "vendor_name", header: "Their name", width: "minmax(110px,1fr)", optional: true, edit: text("vendor_name") },
      {
        key: "spec",
        header: "Spec",
        // Room for the "released after" chip only while a line has one; Packaging --- and for packaging lines' print files;
        // Product labels --- and for a label part's size and link, or a product's Label link (and its warn chip).
        width: [...specs.values()].some((s) => s.newer)
          ? "300px"
          : packing.size
            ? [...labels.values()].some((x) => x.kind === "part")
              ? "400px"
              : "340px"
            : labels.size
              ? [...labels.values()].some((x) => x.warnings.length)
                ? "144px"
                : "120px"
              : "64px",
        value: (l) => specs.get(l.id)?.revision?.rev ?? "",
        cell: (l) => {
          const s = specs.get(l.id);
          const p = packing.get(l.id);
          const lab = labels.get(l.id);
          if (p) return lab?.kind === "part" ? labelPartCell(s, p, lab) : packCell(s, p);
          const link = lab?.kind === "product" && labelsLink(l, lab);
          if (!s?.revision && !s?.newer) return link || <span className="text-faint">—</span>;
          return (
            <span className="flex min-w-0 items-center gap-1">
              {s.revision && rev(s, s.revision)}
              {link}
              {newer(s)}
            </span>
          );
        },
      },
      {
        key: "qty",
        header: "Qty",
        width: "104px",
        align: "right",
        cell: (l) => {
          const link = terms.get(l.item_id);
          const warnings = qtyWarnings(link, Number(l.qty));
          return (
            <>
              {warnings.length > 0 && (
                <button
                  type="button"
                  className={cx("rounded p-0.5 text-warn", editable && "hover:bg-warn-soft")}
                  title={`${warnings.map((w) => w.message).join(". ")}.${editable ? ` Click for ${qty(warnings[0].fix)}.` : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editable) saveQty(l, link, warnings[0].fix, charges.get(l.id));
                  }}
                >
                  <AlertTriangle className="size-3.5" />
                </button>
              )}
              <span>{qty(l.qty)}</span>
            </>
          );
        },
        edit: {
          get: (l) => String(l.qty),
          save: (l, value) => {
            const n = parseNumber(value);
            if (n == null || n <= 0) return toast.error("Quantity must be more than 0");
            saveQty(l, terms.get(l.item_id), n, charges.get(l.id));
          },
        },
      },
      // Stock everywhere --- what's in stock and still coming, beside what this PO orders
      // Stock everywhere --- in Columns, not shown at first (Mitch, 2026-11-04: "so many columns enabled by default that don't
      // seem to help anything"): a line starts as SKU, name, spec, qty, price and amount
      ...stockColumns<PoLine>(stockIx, (l) => l.item_id).map((c) => ({ ...c, optional: true })),
      {
        key: "unit_price",
        header: `Unit ${po.currency}`,
        width: "160px", // room for "Billed $0.72" beside the PO's price
        align: "right",
        cell: (l) => {
          const link = terms.get(l.item_id);
          const other = link && link.currency !== po.currency;
          if (l.unit_price == null) return <span className="text-warn">No price</span>;
          const flag = priceFlag(cx_, po, l);
          return (
            <span className="flex items-center justify-end gap-1">
              {flag && (
                <span className="rounded bg-warn-soft px-1 text-[10px] font-medium text-warn" title={`${flag.text}: ${flag.plannedText}`}>
                  +{Math.round(flag.pct)}%
                </span>
              )}
              <span
                className={other ? "text-warn" : ""}
                title={
                  charges.get(l.id)?.length
                    ? "From its charges: change them under Builds"
                    : other
                      ? `The vendor's price list is in ${link.currency}`
                      : undefined
                }
              >
                {money(l.unit_price, po.currency)}
              </span>
              {/* An accepted invoice price is the cost --- what its bills charge, accepted (po_line.billed_price) */}
              {l.billed_price != null && (
                <span
                  className="rounded bg-accent-soft px-1 text-[10px] font-medium text-accent-text"
                  title={`Billed at ${money(l.billed_price, po.currency)} a unit (the PO says ${money(l.unit_price, po.currency)}): the price on its accepted bills, which costs use`}
                >
                  Billed {money(l.billed_price, po.currency)}
                </span>
              )}
            </span>
          );
        },
        edit: {
          get: (l) => (l.unit_price == null ? "" : String(l.unit_price)),
          save: (l, value) => {
            const n = parseNumber(value);
            if (value.trim() && (n == null || n < 0)) return toast.error("Price must be 0 or more");
            mutate("po_line.update", { id: l.id, patch: { unit_price: n } });
          },
          can: (l) => !charges.get(l.id)?.length, // Charges --- priced by its charges
        },
      },
      {
        key: "amount",
        header: "Amount",
        width: "100px",
        align: "right",
        value: (l) => lineAmount(Number(l.qty), l.unit_price),
        cell: (l) => <span className="font-medium">{money(lineAmount(Number(l.qty), l.unit_price), po.currency)}</span>,
      },
      {
        key: "promised_date",
        header: "Promised",
        width: "96px",
        optional: true,
        cell: (l) => <span className="text-muted">{l.promised_date ?? ""}</span>,
        edit: {
          get: (l) => l.promised_date ?? "",
          save: (l, value) => {
            const v = value.trim();
            if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return toast.error("Use a date like 2026-12-31");
            mutate("po_line.update", { id: l.id, patch: { promised_date: v || null } });
          },
        },
      },
      {
        key: "notes",
        header: "Line notes",
        width: "minmax(100px,1fr)",
        optional: true,
        cell: (l) => <span className="truncate text-muted">{l.notes}</span>,
        edit: text("notes"),
      },
      {
        key: "remove",
        header: "",
        width: "32px",
        value: () => "",
        cell: (l) =>
          editable &&
          can("po_line.remove") && (
            <button
              type="button"
              className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
              title="Remove this line"
              onClick={(e) => {
                e.stopPropagation();
                mutate("po_line.remove", { id: l.id });
              }}
            >
              <Trash2 className="size-3.5" />
            </button>
          ),
      },
    ];
  }, [po, terms, editable, specs, packing, labels, cx_, itemById, charges, stockIx]);

  return (
    <DataTable
      fit={fit}
      rows={lines}
      columns={columns}
      defaultSort={{ key: "position" }}
      sectionOf={packing.size ? sectionOf : undefined}
      editing={editable}
      rowNoun={["line", "lines"]}
      exportName="PO lines"
      bar={bar}
      pageFilters={false}
      empty={<div className="p-6 text-center text-muted">{editable ? "No lines yet. Search for an item above, or add this vendor's items." : "No lines."}</div>}
    />
  );
}

/**
 * Product labels --- what's off about a line's labels (labelContent()'s warnings) as a small warn chip: its title lists
 * them, a click opens the label part (its Packaging section shows each product's label and what's off).
 */
export function LabelWarnChip({ warnings, label }: { warnings: readonly string[]; label: Item }) {
  const openLabel = useOpenLabel();
  if (!warnings.length) return null;
  return (
    <button
      type="button"
      className="inline-flex h-4 shrink-0 items-center gap-0.5 rounded bg-warn-soft px-1 text-[10px] font-medium text-warn hover:underline"
      title={`${warnings.join("\n")}\n\n${LABEL_FIX_TITLE}`}
      onClick={(e) => {
        e.stopPropagation();
        openLabel({ label });
      }}
    >
      <AlertTriangle className="size-3" />
      {warnings.length > 1 && warnings.length}
    </button>
  );
}

type Hit = { item: Item; link: VendorItem | undefined };

/** Type a SKU, name, barcode or the vendor's SKU; Enter adds the top match (this vendor's items first). */
export function AddItemSearch({ po, lines, terms }: { po: Po; lines: PoLine[]; terms: Terms }) {
  const items = useRows("item");
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const hits = useMemo<Hit[]>(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const exact = query.trim().toLowerCase();
    const out: (Hit & { rank: number })[] = [];
    for (const item of items) {
      if (item.archived) continue;
      const link = terms.get(item.id);
      const hay = `${item.sku} ${item.name} ${item.barcode} ${link?.vendor_sku ?? ""} ${link?.vendor_name ?? ""}`.toLowerCase();
      if (!words.every((w) => hay.includes(w))) continue;
      const rank = (item.sku.toLowerCase() === exact || link?.vendor_sku.toLowerCase() === exact ? 0 : 2) + (link ? 0 : 1);
      out.push({ item, link, rank });
    }
    return out.sort((a, b) => a.rank - b.rank || a.item.sku.localeCompare(b.item.sku, undefined, { numeric: true })).slice(0, 8);
  }, [query, items, terms]);

  const pick = async (hit: Hit | undefined) => {
    if (!hit) return;
    setQuery("");
    setCursor(0);
    if (!hit.link) toast.info(`${hit.item.sku || hit.item.name} isn't linked to this vendor: add its price and vendor SKU on the line`);
    await addLines(po, lines, [{ item: hit.item, link: hit.link }]);
    input.current?.focus();
  };

  return (
    <div className="relative w-96">
      <Search className="pointer-events-none absolute top-1.5 left-2.5 size-4 text-faint" />
      <Input
        ref={input}
        value={query}
        placeholder="Add an item: SKU, name or the vendor's SKU…"
        className="h-7 pl-8 text-[12px]"
        onChange={(e) => {
          setQuery(e.target.value);
          setCursor(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") setCursor((c) => Math.min(hits.length - 1, c + 1));
          else if (e.key === "ArrowUp") setCursor((c) => Math.max(0, c - 1));
          else if (e.key === "Enter") pick(hits[cursor]);
          else if (e.key === "Escape") {
            setQuery("");
            e.currentTarget.blur();
          } else return;
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      {open && hits.length > 0 && (
        <div className="absolute top-8 left-0 z-30 w-[560px] overflow-hidden rounded-lg border border-line bg-surface shadow-pop">
          {hits.map((h, i) => (
            <button
              key={h.item.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setCursor(i)}
              onClick={() => pick(h)}
              className={cx("flex w-full items-center gap-2 px-2.5 py-1.5 text-left", i === cursor && "bg-hover")}
            >
              <Thumb src={h.item.photo_url} size={22} />
              <span className="w-28 shrink-0 truncate font-mono text-[12px]">{h.item.sku}</span>
              <span className="min-w-0 flex-1 truncate">{h.item.name}</span>
              {h.link ? (
                <span className="shrink-0 font-mono text-[11px] text-muted">
                  {[h.link.vendor_sku, money(h.link.unit_price, h.link.currency), h.link.moq ? `MOQ ${qty(h.link.moq)}` : ""].filter(Boolean).join(" · ")}
                  {` · adds ${qty(validQty(h.link))}`}
                </span>
              ) : (
                <span className="shrink-0 text-[11px] text-faint">not from this vendor</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
